import { BorderlessButton, InfoButton, PrimaryButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import BankReconciliationPostMutation from '../../graphql/mutations/bank/BankReconciliationPostMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import {
  AlertType,
  IBankReconciliation,
  IBankReconciliationPostMutationResult,
  IBankReconciliationPostMutationVariables,
} from '../../graphql/schema/p4types';
import { useContext, useState } from 'react';
import { IFormAction } from '../../@types/entityTypes';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import { useHistory } from 'react-router';

export enum BankReconciliationAction {
  AutoMatch = 'BANK_RECONCILIATION_AUTO_MATCH',
  DownloadFromBank = 'BANK_RECONCILIATION_DOWNLOAD_FROM_BANK',
  CustomerReceipts = 'BANK_RECONCILIATION_CUSTOMER_RECEIPTS',
  SupplierPayments = 'BANK_RECONCILIATION_SUPPLIER_PAYMENTS',
  BankReceiptPayment = 'BANK_RECONCILIATION_BANK_PAYMENT_RECEIPT',
  Reconcile = 'BANK_RECONCILIATION_RECONCILE',
  PrintDynamicReport = 'PRINT_DYNAMIC_REPORT',
}

export const formActionsForPermissions = [
  { description: 'Auto Match', actionReference: BankReconciliationAction.AutoMatch },
  { description: 'Download From Bank', actionReference: BankReconciliationAction.DownloadFromBank },
  { description: 'Customer Receipts', actionReference: BankReconciliationAction.CustomerReceipts },
  { description: 'Supplier Payments', actionReference: BankReconciliationAction.SupplierPayments },
  { description: 'Bank Payment/Receipts', actionReference: BankReconciliationAction.BankReceiptPayment },
  { description: 'Reconcile', actionReference: BankReconciliationAction.Reconcile },
  { actionReference: BankReconciliationAction.PrintDynamicReport },
];

const useReconcileAction = (): IFormAction<BankReconciliationAction> => {
  const connectedForm = useContext(ConnectedFormContext);
  const errorHandler = useComponentQueryErrorHandling();
  const [disableReconciliation, setDisableReconciliation] = useState(false);
  const history = useHistory();
  const [performSave] = usePerformSave(
    () => {},
    () => {
      setDisableReconciliation(false);
    }
  );
  const dispatch = useDispatch();
  const [runReconciliation] = useMutation<
    IBankReconciliationPostMutationResult,
    IBankReconciliationPostMutationVariables
  >(BankReconciliationPostMutation, {
    onCompleted: (response) => {
      setDisableReconciliation(false);
      dispatch(serviceActions.genericAlert('Bank Reconciliation', 'Reconciliation Complete', AlertType.Success));

      const pathname = history.location?.pathname;
      if (pathname?.includes('/New') && response?.bankReconciliationPost?.result?.id) {
        history.replace({
          pathname: pathname?.replace('/New', `/${response.bankReconciliationPost.result.id}`),
        });
      } else if (response?.bankReconciliationPost?.result?.id) {
        connectedForm.reloadData?.(true);
      }
    },
    onError: (data) => {
      setDisableReconciliation(false);
      errorHandler(data);
    },
  });

  const reconcile = () => {
    if (hasValidationErrors(connectedForm)) return;
    setDisableReconciliation(true);

    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }: { result: IBankReconciliation }) => {
        runReconciliation({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
            },
          },
        });
      },
    });
  };

  return {
    text: 'Reconcile',
    onClick: reconcile,
    individualButton: true,
    individualButtonComponent: PrimaryButton,
    condition: canBeReconciled,
    disabled:
      disableReconciliation ||
      !(connectedForm.values as IBankReconciliation).reconciliationLines ||
      (connectedForm.values as IBankReconciliation).reconciliationLines.length === 0,
    actionReference: BankReconciliationAction.Reconcile,
  };
};

export const useBankReconciliationActions = ({
  autoMatch,
  downloadBankTransactions,
}: {
  autoMatch: () => void;
  downloadBankTransactions: () => void;
}) => {
  const connectedForm = useContext(ConnectedFormContext);
  const values = connectedForm.values as IBankReconciliation;
  const { bankFeed } = values;
  const bankReconciliationAction = useReconcileAction();

  return [
    {
      description: 'Customer Receipts',
      component: InfoButton,
      fn: openFormActionInNewTab('/Customers/Receipt/Details/New'),
      individualButton: true,
      actionReference: BankReconciliationAction.CustomerReceipts,
    },
    {
      description: 'Supplier Payments',
      component: InfoButton,
      fn: openFormActionInNewTab('/Suppliers/Payment/Details/New'),
      individualButton: true,
      actionReference: BankReconciliationAction.SupplierPayments,
    },
    {
      description: 'Bank Payment/Receipts',
      component: InfoButton,
      fn: openFormActionInNewTab('/Bank/PaymentReceipt/Details/New'),
      individualButton: true,
      actionReference: BankReconciliationAction.BankReceiptPayment,
    },
    bankReconciliationAction,
    {
      description: 'Auto Match',
      fn: autoMatch,
      individualButton: false,
      component: BorderlessButton,
      alignLeft: true,
      condition: () => !connectedForm.readOnly && bankFeed,
    },
    {
      description: 'Download From Bank',
      fn: downloadBankTransactions,
      individualButton: false,
      component: BorderlessButton,
      alignLeft: true,
      condition: () => !connectedForm.readOnly && bankFeed,
    },
  ];
};

// Will return a function that will open a new tab
function openFormActionInNewTab(path: string) {
  return function () {
    window.open(window.location.origin + path, '_blank');
  };
}

function canBeReconciled(values: IBankReconciliation) {
  if (values && values.documentStatus) {
    return values.documentStatus === 'DRAFT';
  }
  return false;
}
